import { Suspense, useEffect, useState } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider } from '@/lib/auth-context'
import { PromoCodeProvider } from '@/hooks/use-promo-code'
import { useTranslation } from 'react-i18next'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { router } from './router'
import { detectLocale } from '@/lib/i18n'

import './App.css'
import './i18n/config'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity, // Cache for 1 minute by default
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  },
})


// Root component that ensures language is ready before rendering
function App() {
  const [isLangReady, setIsLangReady] = useState(false)
  const { i18n } = useTranslation()

  useEffect(() => {
    window.clientParamBuilder?.processAndCollectAllParams(window.location.href);
  }, []);

  useEffect(() => {
    // Get the current path
    const path = window.location.pathname
    const browserLang = navigator.language

    // Determine the correct language
    let targetLang = 'en'
    if (path.startsWith('/zh-TW')) {
      targetLang = 'zh-TW'
    } else if (path.startsWith('/ko')) {
      targetLang = 'ko'
    } else if (path.startsWith('/ja')) {
      targetLang = 'ja'
    } else if (!path.startsWith('/en')) {
      targetLang = detectLocale(browserLang)
    }

    // Set the language and mark as ready
    i18n.changeLanguage(targetLang).then(() => {
      setIsLangReady(true)
    })
  }, [i18n])

  if (!isLangReady) {
    return null // Or a loading spinner
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <PromoCodeProvider>
          <AuthProvider>
            <Suspense fallback={null}>
              <RouterProvider router={router} />
              <Toaster />
            </Suspense>
          </AuthProvider>
        </PromoCodeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
